import { computed, ref } from 'vue';

/**
 * Step 3 state: corner shape + bend angle.
 *
 * MINIMAL: shape selection drives the preview; the angle slider is wired but
 * not yet applied to the canvas geometry (deferred).
 */
export type JointShape = 'external' | 'internal';

export const JOINT_ANGLE_MIN = 1;
export const JOINT_ANGLE_MAX = 90;
export const JOINT_ANGLE_DEFAULT = 90;
export const JOINT_SHAPE_DEFAULT: JointShape = 'external';

export function useShapeAndAngle() {
    const selectedShape = ref<JointShape | null>(JOINT_SHAPE_DEFAULT);
    const angle = ref<number>(JOINT_ANGLE_DEFAULT);
    const angleInputRenderKey = ref<number>(0);

    const isShapeAndAngleCompleted = computed<boolean>(() => selectedShape.value !== null);

    function setSelectedShape(shape: JointShape | null) {
        selectedShape.value = shape;
    }

    function setAngle(nextAngle: number) {
        if (isNaN(nextAngle)) {
            return;
        }
        const rounded = Math.round(nextAngle * 10) / 10;
        const clamped = Math.min(JOINT_ANGLE_MAX, Math.max(JOINT_ANGLE_MIN, rounded));

        if (clamped === angle.value) {
            angleInputRenderKey.value += 1;
            return;
        }

        angle.value = clamped;
    }

    function resetShapeAndAngle() {
        selectedShape.value = JOINT_SHAPE_DEFAULT;
        angle.value = JOINT_ANGLE_DEFAULT;
    }

    return {
        // State
        selectedShape,
        angle,
        angleInputRenderKey,
        // Completion
        isShapeAndAngleCompleted,
        // Mutations
        setSelectedShape,
        setAngle,
        resetShapeAndAngle,
    };
}
